import { useRef, useState, type DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { Button, Text } from "@athena/ui";
import { cn } from "@athena/ui/utils";
import { CloudUpload, Plus } from "lucide-react";

interface UploadDropZoneProps {
    onFiles: (files: File[]) => void;
    /**
     * `hero` is the big empty-state target. `button` is the header's "Add videos" once the queue
     * has something in it — the whole page takes drops by then, so a second zone would only cost
     * the queue its room.
     */
    variant?: "hero" | "button";
    disabled?: boolean;
}

/** Where videos come in: drop them, or click to browse. */
export function UploadDropZone({ onFiles, variant = "hero", disabled = false }: UploadDropZoneProps) {
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement>(null);
    /**
     * Counted rather than a boolean: dragging over a child fires `dragleave` on the parent, and a
     * boolean flickers the highlight off and on as the pointer crosses the icon.
     */
    const [
        dragDepth,
        setDragDepth,
    ] = useState<number>(0);
    const dragging = dragDepth > 0 && !disabled;

    const input = (
        <input
            ref={inputRef}
            type="file"
            accept="video/*"
            multiple
            disabled={disabled}
            className="sr-only"
            tabIndex={-1}
            aria-hidden
            data-testid="bulk-upload-input"
            onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                // Cleared so choosing the same file again after removing it still fires.
                e.target.value = "";
                if (files.length) onFiles(files);
            }}
        />
    );

    if (variant === "button") {
        return (
            <>
                {input}
                <Button className="w-auto" disabled={disabled} onClick={() => inputRef.current?.click()}>
                    <Plus className="mr-1.5 h-4 w-4" />
                    {t("admin.bulkUpload.addVideos")}
                </Button>
            </>
        );
    }

    return (
        <>
            {input}
            <button
                type="button"
                disabled={disabled}
                onClick={() => inputRef.current?.click()}
                onDragEnter={(e: DragEvent) => {
                    e.preventDefault();
                    setDragDepth((d) => d + 1);
                }}
                onDragOver={(e) => e.preventDefault()}
                onDragLeave={(e: DragEvent) => {
                    e.preventDefault();
                    setDragDepth((d) => Math.max(0, d - 1));
                }}
                onDrop={(e: DragEvent) => {
                    e.preventDefault();
                    setDragDepth(0);
                    if (disabled) return;
                    const files = Array.from(e.dataTransfer.files);
                    if (files.length) onFiles(files);
                }}
                className={cn(
                    "group border-border bg-card flex w-full flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed px-6 py-20 text-center transition-all duration-200",
                    "hover:border-primary/60 hover:bg-primary/5 focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
                    "disabled:cursor-not-allowed disabled:opacity-60",
                    dragging && "border-primary bg-primary/10 scale-[1.01]",
                )}
            >
                <span
                    className={cn(
                        "bg-primary/10 text-primary flex h-16 w-16 items-center justify-center rounded-full transition-transform duration-200 group-hover:scale-105",
                        dragging && "scale-110",
                    )}
                >
                    <CloudUpload className="h-8 w-8" />
                </span>
                <span className="flex flex-col gap-1">
                    <Text as="span" weight="semibold" size="lg">
                        {dragging ? t("admin.bulkUpload.dropActive") : t("admin.bulkUpload.dropTitle")}
                    </Text>
                    <Text as="span" size="sm" muted>
                        <span className="text-primary font-medium underline-offset-4 group-hover:underline">
                            {t("admin.bulkUpload.dropBrowse")}
                        </span>
                        {" · "}
                        {t("admin.bulkUpload.dropHint")}
                    </Text>
                </span>
            </button>
        </>
    );
}
